/**
 * Reports what a person does in the mini app to POST /api/v1/me/events, for the door's «Events» tab: opening it,
 * each screen, each pressed control, scroll marks per screen, leaving and coming back. Mounted once, in the auth gate.
 *
 * Fire and forget: the opening goes out at once, so a short visit is not lost; after it, batches every 2 s, one
 * request at a time, so they keep their order. A lost batch is not sent again, and nothing here blocks or breaks the
 * app. Leaving the app sends at once with `keepalive`, without waiting for the line, so the request outlives the page.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { apiFetch } from '../lib/api-client';
import { clickedControl, maskStartParam, maskToken, reachedMarks } from '../lib/user-events';
import { useAuth } from '../store/auth';

type EventName = 'app_open' | 'app_screen' | 'app_click' | 'app_scroll' | 'app_hidden' | 'app_visible';
interface QueuedEvent { name: EventName; props: Record<string, string | number> }

const FLUSH_MS = 2000;
const BATCH = 30;
/** Kept while the session is not ready; past this the oldest go. */
const MAX_QUEUE = 300;

function startParam(): string | undefined {
  const tg = window.Telegram as { WebApp?: { initDataUnsafe?: { start_param?: string } } } | undefined;
  const param = tg?.WebApp?.initDataUnsafe?.start_param;
  return param ? maskStartParam(param) : undefined;
}

export function useUserEvents(): void {
  const jwt = useAuth((s) => s.jwt);
  const { pathname } = useLocation();
  const screen = maskToken(pathname) || '/';

  const jwtRef = useRef(jwt);
  const screenRef = useRef(screen);
  const queue = useRef<QueuedEvent[]>([]);
  const sending = useRef<Promise<void>>(Promise.resolve());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const opened = useRef(false);
  const lastScreen = useRef<string | null>(null);
  const marksSent = useRef(new Set<number>());
  const visibleSince = useRef(Date.now());
  jwtRef.current = jwt;
  screenRef.current = screen;

  const api = useRef({
    flush(keepalive = false): void {
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
      const token = jwtRef.current;
      if (!token) return;
      while (queue.current.length > 0) {
        const events = queue.current.splice(0, BATCH);
        const send = async (): Promise<void> => {
          try {
            await apiFetch('/api/v1/me/events', token, {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ events }),
              keepalive,
            });
          } catch { /* tracking never disturbs the app */ }
        };
        if (keepalive) void send();
        else sending.current = sending.current.then(send);
      }
    },
    push(name: EventName, props: QueuedEvent['props']): void {
      queue.current.push({ name, props });
      if (queue.current.length > MAX_QUEUE) queue.current.shift();
      if (!timer.current) timer.current = setTimeout(() => api.current.flush(), FLUSH_MS);
    },
  });

  // The first event once there is a session; the queue keeps it until then.
  useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    const param = startParam();
    api.current.push('app_open', param ? { startParam: param } : {});
  }, []);

  useEffect(() => {
    if (lastScreen.current === screen) return;
    lastScreen.current = screen;
    marksSent.current = new Set();
    visibleSince.current = Date.now();
    api.current.push('app_screen', { screen });
  }, [screen]);

  // A session that arrives after the first events sends them now.
  useEffect(() => {
    if (jwt && queue.current.length > 0) api.current.flush();
  }, [jwt]);

  useEffect(() => {
    let frame = 0;
    let scroller: Element | null = null;

    const onClick = (e: MouseEvent): void => {
      try {
        const button = clickedControl(e.target);
        if (button) api.current.push('app_click', { screen: screenRef.current, button });
      } catch { /* a broken lookup skips this press */ }
    };
    const check = (): void => {
      frame = 0;
      const el = scroller;
      if (!el) return;
      for (const depth of reachedMarks(el, marksSent.current)) {
        marksSent.current.add(depth);
        api.current.push('app_scroll', { screen: screenRef.current, depth });
      }
    };
    const onScroll = (e: Event): void => {
      scroller = e.target instanceof Element ? e.target : document.scrollingElement;
      if (!frame) frame = window.requestAnimationFrame(check);
    };
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') {
        const seconds = Math.min(86_400, Math.round((Date.now() - visibleSince.current) / 1000));
        api.current.push('app_hidden', { screen: screenRef.current, seconds });
        api.current.flush(true);
      } else {
        visibleSince.current = Date.now();
        api.current.push('app_visible', { screen: screenRef.current });
      }
    };
    const onPageHide = (): void => api.current.flush(true);

    document.addEventListener('click', onClick, { capture: true });
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('click', onClick, { capture: true });
      document.removeEventListener('scroll', onScroll, { capture: true });
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      if (frame) window.cancelAnimationFrame(frame);
      api.current.flush();
    };
  }, []);
}
